/**
 * Agent Avatar Feature - TypeScript Interfaces
 */

export type AvatarCategory = 'robot' | 'anime' | 'human' | 'abstract';
export type AvatarType = 'image' | 'video';
export type AvatarStatus = 'active' | 'inactive' | 'all';

export interface AvatarFile {
  publicId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  fileKey: string;
}

export interface AgentAvatar {
  publicId: string;
  name: string;
  category: string | null;
  tags: string | null; // JSON array string e.g. '["friendly","serious"]'
  type: string;
  previewUrl: string | null;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  file: AvatarFile;
}

export interface AvatarListMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ListAvatarsResponse {
  success: boolean;
  message: string;
  data: AgentAvatar[];
  meta: AvatarListMeta;
}

export interface AvatarResponse {
  success: boolean;
  message: string;
  data: AgentAvatar;
}

export interface ListAvatarsParams {
  page?: number;
  limit?: number;
  status?: AvatarStatus;
  category?: AvatarCategory | '';
  type?: AvatarType | '';
}

export interface UploadAvatarPayload {
  file: File;
  name: string;
  category?: AvatarCategory;
  tags?: string; // JSON array string
  isDefault?: boolean;
}

export interface UpdateAvatarPayload {
  publicId: string;
  name?: string;
  category?: AvatarCategory;
  tags?: string;
  isDefault?: boolean;
  previewUrl?: string;
}

export interface ToggleAvatarStatusPayload {
  publicId: string;
  isActive: boolean;
}

/** Parsed tags from JSON string */
export function parseTags(tagsJson: string | null): string[] {
  if (!tagsJson) return [];
  try {
    const parsed = JSON.parse(tagsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Format file size in human-readable format */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Category display labels */
export const CATEGORY_LABELS: Record<AvatarCategory, string> = {
  robot: 'Robot',
  anime: 'Anime',
  human: 'Human',
  abstract: 'Abstract',
};

/** Category color map for badges */
export const CATEGORY_COLORS: Record<AvatarCategory, string> = {
  robot: '#3B82F6',
  anime: '#EC4899',
  human: '#10B981',
  abstract: '#8B5CF6',
};
